import { useMemo } from 'react';
import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';
import RemoveIcon from '@mui/icons-material/Remove';

// ── Industry benchmarks (in days) ──
const BENCHMARKS = {
  origination: { good: 5, ok: 10 },
  setupToUW: { good: 3, ok: 5 },
  submissionToDecision: { good: 2, ok: 4 },
  clearToClose: { good: 5, ok: 10 },
  totalCycle: { good: 25, ok: 35 },
};

const daysBetween = (start, end) => {
  if (!start || !end) return null;
  const ms = new Date(end) - new Date(start);
  return Math.round(ms / (1000 * 60 * 60 * 24) * 10) / 10;
};

const daysSince = (date) => {
  if (!date) return null;
  return daysBetween(date, new Date());
};

const getIndicator = (days, benchmark) => {
  if (days == null || !benchmark) return { color: 'text.secondary', Icon: RemoveIcon };
  if (days <= benchmark.good) return { color: 'success.main', Icon: CheckCircleIcon };
  if (days <= benchmark.ok) return { color: 'warning.main', Icon: WarningIcon };
  return { color: 'error.main', Icon: ErrorIcon };
};

const formatDuration = (days) => {
  if (days == null) return '—';
  if (days < 1) return '< 1 day';
  if (days === 1) return '1 day';
  return `${days} days`;
};

const LoanVelocityTiles = ({ loanDetail }) => {
  const stages = loanDetail?.loanStages;
  const loan = loanDetail?.loan || {};
  const keyDates = loanDetail?.keyDatesObj || {};

  const stageMap = useMemo(() => {
    if (!stages) return {};
    const map = {};
    for (const s of stages) {
      map[s.templateName] = s;
    }
    return map;
  }, [stages]);

  const metrics = useMemo(() => {
    const origStart = stageMap['Origination']?.startDate;
    const procStart = stageMap['Processing']?.startDate;
    const uwStart = stageMap['Underwriting']?.startDate;
    const setupStart = stageMap['Setup']?.startDate;
    const fundingStart = stageMap['Funding']?.startDate;
    const ctcDate = keyDates.clearedToCloseDate;
    const fundedDate = keyDates.fundedDate;
    const currentStageDate = loanDetail?.currentStageChangedDate;

    return [
      {
        key: 'origination',
        label: 'Import → Setup',
        sublabel: 'Broker prep before submission',
        days: daysBetween(origStart, procStart),
        benchmark: BENCHMARKS.origination,
        industryAvg: '7–10 days',
      },
      {
        key: 'setupToUW',
        label: 'Setup → UW',
        sublabel: 'Processing → Underwriting',
        days: daysBetween(procStart || setupStart, uwStart),
        benchmark: BENCHMARKS.setupToUW,
        industryAvg: '3–5 days',
      },
      {
        key: 'submissionToDecision',
        label: 'UW Decision',
        sublabel: 'Submission → Decision',
        days: uwStart && ctcDate ? daysBetween(uwStart, ctcDate) : null,
        benchmark: BENCHMARKS.submissionToDecision,
        industryAvg: '2–3 days',
      },
      {
        key: 'clearToClose',
        label: 'Clear to Close',
        sublabel: 'UW complete → CTC',
        days: uwStart && ctcDate ? daysBetween(uwStart, ctcDate) : null,
        benchmark: BENCHMARKS.clearToClose,
        industryAvg: '5–10 days',
      },
      {
        key: 'totalCycle',
        label: 'Total Cycle',
        sublabel: 'Origination → Funded',
        days: origStart ? daysBetween(origStart, fundedDate || new Date()) : null,
        benchmark: BENCHMARKS.totalCycle,
        industryAvg: '30–45 days',
        isOngoing: !fundedDate,
      },
    ];
  }, [stageMap, keyDates, loan.stage, loanDetail]);

  if (!stages || stages.length === 0) return null;

  return (
    <Grid container spacing={2} sx={{ mb: 2 }}>
      {metrics.map(m => {
        const ind = getIndicator(m.days, m.benchmark);
        const IconComp = ind.Icon;

        return (
          <Grid key={m.key} size={{ xs: 6, sm: 4, lg: 2.4 }}>
            <Card variant="outlined">
              <CardContent sx={{ py: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: `${ind.color.split('.')[0]}.50` }}>
                    <IconComp sx={{ fontSize: 16, color: ind.color }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" fontWeight={700}>{m.label}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.65rem', lineHeight: 1.2 }}>{m.sublabel}</Typography>
                  </Box>
                </Stack>
                <Typography variant="h6" color={ind.color} sx={{ fontSize: '1.3rem' }}>
                  {formatDuration(m.days)}
                  {m.isOngoing && m.days != null && (
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>ongoing</Typography>
                  )}
                </Typography>
                {m.industryAvg && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>Industry avg: {m.industryAvg}</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default LoanVelocityTiles;
